import { useLiveQuery } from 'dexie-react-hooks'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage } from '../../api/client'
import type { User } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import { useDebounced } from '../../hooks/useDebounced'
import { isBotUser } from '../../lib/bots'
import { isAdminRole, useSession } from '../../state/session'
import { useUi } from '../../state/ui'
import { openDirectMessage } from './createConversation'
import { useDirectorySearch } from './directorySearch'
import { PersonText } from './PersonText'
import { recentContacts } from './recentContacts'

const NOBODY: User[] = []

export function NewDmDialog() {
  const open = useUi((s) => s.dialog === 'new-dm')
  const close = useUi((s) => s.closeDialog)
  return open ? <NewDmForm onClose={close} /> : null
}

/**
 * New Message is a list of people, the way a phone's own Messages and WhatsApp do it: almost every visit is to message
 * someone, so a tap on a name opens the conversation (there is nothing to confirm; the server hands back the existing
 * one rather than making a second). Recent conversations come first, then everyone, then bots. Starting a group, or
 * for an administrator a channel, is a row at the head of the list, out of the way once a name is typed.
 *
 * It opens on the list with the keyboard down, as WhatsApp does. Searching puts the keyboard up, and an iPhone then
 * leaves under half the screen: the group and channel rows step aside for names, only the bar and the search stay put,
 * the list is the one thing that scrolls, and dragging it puts the keyboard away, as a native list does.
 */
function NewDmForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const meId = useSession((s) => s.me?.id)
  const isAdmin = useSession((s) => isAdminRole(s.me))
  const openDialog = useUi((s) => s.openDialog)
  const [query, setQuery] = useState('')
  const [opening, setOpening] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // Searching from the moment the field is focused. Blurring does not end it: a tap on a name blurs the field first, and
  // the rows coming back then would move the name out from under the finger. Dragging the list with nothing typed does.
  const [searching, setSearching] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const typed = query.trim()
  const debounced = useDebounced(typed, 250)
  const { results, status, error, offline } = useDirectorySearch(debounced, true, 50)
  const recent = useLiveQuery(() => (meId ? recentContacts(meId) : NOBODY), [meId], NOBODY)

  const found = results.filter((u) => u.id !== meId)
  const people = found.filter((u) => !isBotUser(u))
  const bots = found.filter(isBotUser)
  const first = typed ? found[0] : (recent[0] ?? people[0] ?? bots[0])
  // What the list shows is for the last query sent, so wait for the one just typed before calling it empty.
  const settled = status !== 'loading' && debounced === typed

  const open = async (user: User) => {
    if (!meId || opening) return
    setOpening(user.id)
    setFailure(null)
    try {
      const id = await openDirectMessage(meId, user.id)
      onClose()
      navigate(`/c/${id}`)
    } catch (e) {
      setFailure(errorMessage(e))
      setOpening(null)
    }
  }

  const section = (title: string, users: User[], hideTitle = false) =>
    users.length ? (
      <section className="nm-section" aria-labelledby={`nm-${title}`}>
        <h3 id={`nm-${title}`} className={hideTitle ? 'sr-only' : 'nm-heading'}>
          {title}
        </h3>
        <ul aria-labelledby={`nm-${title}`}>
          {users.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                className="picker-person nm-row"
                aria-busy={opening === user.id}
                disabled={opening !== null && opening !== user.id}
                onClick={() => void open(user)}
              >
                <Avatar user={user} />
                <PersonText user={user} />
                {opening === user.id ? <span className="spinner" aria-hidden="true" /> : null}
              </button>
            </li>
          ))}
        </ul>
      </section>
    ) : null

  const announcement = !settled
    ? ''
    : typed
      ? found.length
        ? `${found.length} ${found.length === 1 ? 'result' : 'results'}`
        : 'No results'
      : `${recent.length ? `${recent.length} recent, ` : ''}${people.length} people, ${bots.length} bots`

  return (
    <Dialog chrome="bar" open onClose={onClose} title="New Message" titleId="new-dm-title" description="Find a colleague or a bot to start a conversation." className="new-dialog new-message-dialog">
      <div className="nm-search">
        <div className="search-field">
          <Icon name="search" />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search staff and bots"
            aria-label="Search staff and bots"
            aria-controls="nm-list"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setSearching(true)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              if (first) void open(first)
            }}
          />
        </div>
        {failure ? (
          <p role="alert" className="field-error nm-note">
            {failure}
          </p>
        ) : offline && status === 'idle' ? (
          <p className="nm-note">Showing people known on this phone</p>
        ) : null}
      </div>
      <div
        className="nm-list"
        id="nm-list"
        // A drag through the list puts the keyboard away and gives the list the whole screen.
        onTouchMove={() => {
          if (document.activeElement === searchRef.current) searchRef.current?.blur()
          if (!typed) setSearching(false)
        }}
      >
        {typed || searching ? null : (
          <div className="nm-create">
            {/* Kept as .create-entry: the browser suites find the rows by it. */}
            <button type="button" className="create-entry nm-row" aria-label="New group" onClick={() => openDialog('new-group')}>
              <span className="nm-create-icon" aria-hidden="true">
                <Icon name="users" />
              </span>
              <strong>New group</strong>
              <Icon name="chevronRight" />
            </button>
            {/* A channel is official, so an administrator sets it up; a group chat is what anyone can start. */}
            {isAdmin ? (
              <button type="button" className="create-entry nm-row" aria-label="New channel" onClick={() => openDialog('new-channel')}>
                <span className="nm-create-icon" aria-hidden="true">
                  <Icon name="hash" />
                </span>
                <strong>New channel</strong>
                <Icon name="chevronRight" />
              </button>
            ) : null}
          </div>
        )}
        {typed ? (
          section('Results', found, true)
        ) : (
          <>
            {section('Recent', recent)}
            {section('Everyone', people)}
            {section('Bots', bots)}
          </>
        )}
        {status === 'loading' && found.length === 0 ? <p className="picker-status">Searching…</p> : null}
        {settled && status === 'error' ? <p className="picker-status">{error}</p> : null}
        {settled && status === 'idle' && typed && found.length === 0 ? (
          <div className="nm-empty">
            <strong>Nobody called “{typed}”</strong>
            <p>Check the spelling, or ask an administrator to add them.</p>
          </div>
        ) : null}
        {!typed && !isAdmin ? <p className="nm-footnote">Channels are set up by your administrators.</p> : null}
      </div>
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </Dialog>
  )
}
